/**
 * The ligand interaction diagram of an MD job's results: the ligand with the
 * residues that touch it, and the fraction of the run each interaction lasts
 * (integrations/mdInteractionDiagram.js builds the picture; reports reuse it).
 * It is a plain inline SVG on a white panel, so it needs no theme and does not
 * take the mouse wheel.
 */
import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

import Select from 'BMapsUI/common/Select';
import {
    DIAGRAM_THRESHOLDS, DEFAULT_DIAGRAM_THRESHOLD, diagramModel, hasInteractionDiagram,
    interactionDiagramCaption, interactionDiagramSvg, interactionRuns,
} from 'BMapsSrc/integrations/mdInteractionDiagram';

export default function LigandInteractionDiagram({ compound }) {
    const runs = useMemo(() => interactionRuns(compound), [compound]);
    const [run, setRun] = useState('mean');
    const [threshold, setThreshold] = useState(DEFAULT_DIAGRAM_THRESHOLD);
    const idPrefix = `lid-${String(compound?.name || '').replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const svg = useMemo(() => interactionDiagramSvg(diagramModel(compound, {
        run: run === 'mean' ? 'mean' : Number(run), threshold, idPrefix,
    })), [compound, run, threshold, idPrefix]);
    const id = `md-lid-${compound?.name}`;
    // Say so, rather than leave the section out: results from before the contact
    // analysis was added (or an analysis step that failed) carry no data for it.
    if (!hasInteractionDiagram(compound)) {
        return (
            <Typography
                id={`${id}-missing`}
                variant="caption"
                component="div"
                sx={{ marginTop: '0.75rem', opacity: 0.8 }}
            >
                No ligand–protein contact data in these results, so there is no interaction diagram.
            </Typography>
        );
    }
    return (
        <Box id={id} sx={{ marginTop: '0.75rem' }}>
            <Typography component="div" sx={{ fontWeight: 600 }}>
                Ligand–protein contacts
            </Typography>
            <Box sx={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {runs.length > 1 && (
                    <Select
                        id={`${id}-run`}
                        label="Runs shown"
                        value={run}
                        onChange={(e) => setRun(e.target.value)}
                        sx={{ flex: 1, margin: '0.25rem 0', minWidth: '10rem' }}
                    >
                        <option value="mean">Mean of all runs</option>
                        {runs.map((r) => <option key={r} value={String(r)}>{`Run ${r}`}</option>)}
                    </Select>
                )}
                <Select
                    id={`${id}-threshold`}
                    label="Interactions lasting more than"
                    value={String(threshold)}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                    sx={{ flex: 1, margin: '0.25rem 0', minWidth: '10rem' }}
                >
                    {DIAGRAM_THRESHOLDS.map((t) => (
                        <option key={t} value={String(t)}>{`${Math.round(t * 100)}% of the run`}</option>
                    ))}
                </Select>
            </Box>
            <Box
                aria-label={`Ligand interaction diagram of ${compound.name}`}
                sx={{ '& svg': { width: '100%', height: 'auto', display: 'block' } }}
                // Built here from the packet's numbers (text escaped) and RDKit's drawing.
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: svg }}
            />
            <Typography variant="caption" component="div" sx={{ opacity: 0.8 }}>
                {interactionDiagramCaption(threshold)}
            </Typography>
        </Box>
    );
}
